import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { type Paginate, type SearchRequest, type User } from "@/types";
import { getNextPaginatePage } from "@/utils";

import {
  type Objective,
  type ObjectivePeriod,
  type ObjectiveSummary,
  type ObjectiveType,
  type ObjectiveTypesMeSummary,
  type ObjectiveVisibility,
  type ObjectivesStatusSummaryResponse,
  type ObjectivesunitsSummaryResponse,
} from "../types";

export type ObjectivePeriodList = ObjectivePeriod[];
export type ObjectivePeriodUsersList = Paginate<User<true>>;
export type SelectableParentObjectivesList = Paginate<
  Objective<false, false, true>
>;

export const objectivePeriods = createQueryKeys(
  "performance-objective-periods",
  {
    all: (filter?: SearchRequest) => [{ filter }],
    one: (id?: number) => [id],
    users: (objectivePeriodId?: number, filter?: SearchRequest) => [
      objectivePeriodId,
      filter,
    ],
    usersInfinite: (objectivePeriodId?: number, filter?: SearchRequest) => [
      objectivePeriodId,
      filter,
    ],
    selectableOwnerUsersInfinite: (
      objectivePeriodId?: number,
      objectiveTypeId?: number,
      filter?: SearchRequest,
    ) => [objectivePeriodId, objectiveTypeId, filter],
    selectableWatcherUsers: (
      objectivePeriodId?: number,
      userId?: number,
      filter?: SearchRequest,
    ) => [objectivePeriodId, userId, filter],
    selectableWatcherUsersInfinite: (
      objectivePeriodId?: number,
      userId?: number,
      filter?: SearchRequest,
    ) => [objectivePeriodId, userId, filter],
    selectableParentObjectives: (
      objectivePeriodId?: number,
      userId?: number,
      filter?: SearchRequest,
    ) => [objectivePeriodId, userId, filter],
    selectableParentObjectivesInfinite: (
      objectivePeriodId?: number,
      userId?: number,
      filter?: SearchRequest,
    ) => [objectivePeriodId, userId, filter],
    objectivesSummary: (id?: number) => ["objectives-summary", id],
    statusSummary: (id?: number) => ["status-summary", id],
    unitsSummary: (id?: number) => ["unit-summary", id],
    meSummary: (id?: number) => ["me-summary", id],
    objectiveTypes: (objectivePeriodId?: number) => [
      objectivePeriodId,
      "objective-types",
    ],
  },
);

const include = "objectiveTypes.children";
const includeOne = "objectiveTypes.children,participants";

export const fetchObjectivePeriodsQueryOptions = (search?: SearchRequest) =>
  queryOptions({
    ...objectivePeriods.all(search),
    queryFn: () =>
      api
        .get<ObjectivePeriodList>(`/performance/objective-periods`, {
          params: { ...search, include },
        })
        .then((response) => response.data),
  });

export const useFetchObjectivePeriods = (search?: SearchRequest) =>
  useQuery({ ...fetchObjectivePeriodsQueryOptions(search) });

export const fetchObjectivePeriodQueryOptions = (id?: number) =>
  queryOptions({
    ...objectivePeriods.one(id),
    queryFn: () =>
      api
        .get<ObjectivePeriod>(`/performance/objective-periods/${id}`, {
          params: { include: includeOne },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchObjectivePeriod = (id?: number) =>
  useQuery(fetchObjectivePeriodQueryOptions(id));

export const fetchObjectivePeriodUsersQueryOptions = (
  objectivePeriodId: number | undefined,
  search?: SearchRequest,
  includeDirectManagers = false,
) =>
  queryOptions({
    ...objectivePeriods.users(objectivePeriodId, search),
    queryFn: () =>
      api
        .get<ObjectivePeriodUsersList>(
          `/performance/objective-periods/${objectivePeriodId}/users`,
          {
            params: {
              ...search,
              include: includeDirectManagers ? "directManagerUsers" : undefined,
            },
          },
        )
        .then((response) => response.data),
    enabled: objectivePeriodId !== undefined,
  });

export const useFetchObjectivePeriodUsers = (
  objectivePeriodId: number | undefined,
  search?: SearchRequest,
  includeDirectManagers = false,
) =>
  useQuery({
    ...fetchObjectivePeriodUsersQueryOptions(
      objectivePeriodId,
      search,
      includeDirectManagers,
    ),
  });

export const fetchInfiniteObjectivePeriodUsersQueryOptions = (
  objectivePeriodId: number | undefined,
  search?: SearchRequest,
  includeDirectManagers = false,
) =>
  infiniteQueryOptions({
    ...objectivePeriods.usersInfinite(objectivePeriodId, search),
    queryFn: ({ pageParam }) =>
      api
        .get<ObjectivePeriodUsersList>(
          `/performance/objective-periods/${objectivePeriodId}/users`,
          {
            params: {
              ...search,
              page: pageParam,
              include: includeDirectManagers ? "directManagerUsers" : undefined,
            },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
    enabled: objectivePeriodId !== undefined,
  });

export const useFetchInfiniteObjectivePeriodUsers = (
  objectivePeriodId: number | undefined,
  search?: SearchRequest,
  includeDirectManagers = false,
) =>
  useInfiniteQuery(
    fetchInfiniteObjectivePeriodUsersQueryOptions(
      objectivePeriodId,
      search,
      includeDirectManagers,
    ),
  );

export const fetchPeriodObjectiveTypesQueryOptions = (
  objectivePeriodId?: number,
) =>
  queryOptions({
    ...objectivePeriods.objectiveTypes(objectivePeriodId),
    queryFn: () =>
      api
        .get<
          ObjectiveType[]
        >(`/performance/objective-periods/${objectivePeriodId}/objective-types`)
        .then((response) => response.data),
    enabled: objectivePeriodId !== undefined,
  });

export const useFetchPeriodObjectiveTypes = (objectivePeriodId?: number) =>
  useQuery(fetchPeriodObjectiveTypesQueryOptions(objectivePeriodId));

export const fetchInfiniteSelectableOwnerUsersQueryOptions = (
  objectivePeriodId: number | undefined,
  objectiveTypeId: number | undefined,
  search?: SearchRequest,
) =>
  infiniteQueryOptions({
    ...objectivePeriods.selectableOwnerUsersInfinite(
      objectivePeriodId,
      objectiveTypeId,
      search,
    ),
    queryFn: ({ pageParam }) =>
      api
        .get<ObjectivePeriodUsersList>(
          `/performance/objective-periods/${objectivePeriodId}/selectable-owner-users/${objectiveTypeId}`,
          {
            params: {
              ...search,
              page: pageParam,
              include: "position",
            },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
    enabled: objectivePeriodId !== undefined && objectiveTypeId !== undefined,
  });

export const useFetchInfiniteSelectableOwnerUsers = (
  objectivePeriodId: number | undefined,
  objectiveTypeId: number | undefined,
  search?: SearchRequest,
) =>
  useInfiniteQuery(
    fetchInfiniteSelectableOwnerUsersQueryOptions(
      objectivePeriodId,
      objectiveTypeId,
      search,
    ),
  );

export const fetchSelectableWatcherUsersQueryOptions = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectivePeriods.selectableWatcherUsers(
      objectivePeriodId,
      userId,
      search,
    ),
    queryFn: () =>
      api
        .get<ObjectivePeriodUsersList>(
          `/performance/objective-periods/${objectivePeriodId}/users/${userId}/selectable-watcher-users`,
          {
            params: {
              ...search,
            },
          },
        )
        .then((response) => response.data),
    enabled: objectivePeriodId !== undefined && userId !== undefined,
  });

export const useFetchSelectableWatcherUsers = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  search?: SearchRequest,
) =>
  useQuery({
    ...fetchSelectableWatcherUsersQueryOptions(
      objectivePeriodId,
      userId,
      search,
    ),
  });

export const fetchInfiniteSelectableWatcherUsersQueryOptions = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  search?: SearchRequest,
) =>
  infiniteQueryOptions({
    ...objectivePeriods.selectableWatcherUsersInfinite(
      objectivePeriodId,
      userId,
      search,
    ),
    queryFn: ({ pageParam }) =>
      api
        .get<ObjectivePeriodUsersList>(
          `/performance/objective-periods/${objectivePeriodId}/users/${userId}/selectable-watcher-users`,
          {
            params: {
              ...search,
              page: pageParam,
            },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
    enabled: objectivePeriodId !== undefined && userId !== undefined,
  });

export const useFetchInfiniteSelectableWatcherUsers = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  search?: SearchRequest,
) =>
  useInfiniteQuery(
    fetchInfiniteSelectableWatcherUsersQueryOptions(
      objectivePeriodId,
      userId,
      search,
    ),
  );

export const fetchSelectableParentObjectivesQueryOptions = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  visibility?: ObjectiveVisibility,
  search?: SearchRequest,
) => {
  const filteredSearch: SearchRequest = {
    ...search,
    filters: { ...search?.filters, ...(visibility ? { visibility } : {}) },
  };

  return queryOptions({
    ...objectivePeriods.selectableParentObjectives(
      objectivePeriodId,
      userId,
      filteredSearch,
    ),
    queryFn: () =>
      api
        .get<SelectableParentObjectivesList>(
          `/performance/objective-periods/${objectivePeriodId}/users/${userId}/selectable-parent-objectives`,
          {
            params: { ...filteredSearch, include: "user" },
          },
        )
        .then((response) => response.data),
    enabled:
      objectivePeriodId !== undefined &&
      userId !== undefined &&
      visibility !== undefined,
  });
};

export const useFetchSelectableParentObjectives = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  visibility?: ObjectiveVisibility,
  search?: SearchRequest,
) =>
  useQuery({
    ...fetchSelectableParentObjectivesQueryOptions(
      objectivePeriodId,
      userId,
      visibility,
      search,
    ),
  });

export const fetchInfiniteSelectableParentObjectivesQueryOptions = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  visibility?: ObjectiveVisibility,
  search?: SearchRequest,
) => {
  const filteredSearch: SearchRequest = {
    ...search,
    filters: { ...search?.filters, ...(visibility ? { visibility } : {}) },
  };

  return infiniteQueryOptions({
    ...objectivePeriods.selectableParentObjectivesInfinite(
      objectivePeriodId,
      userId,
      filteredSearch,
    ),
    queryFn: ({ pageParam }) =>
      api
        .get<SelectableParentObjectivesList>(
          `/performance/objective-periods/${objectivePeriodId}/users/${userId}/selectable-parent-objectives`,
          {
            params: { ...filteredSearch, page: pageParam, include: "user" },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
    enabled:
      objectivePeriodId !== undefined &&
      userId !== undefined &&
      visibility !== undefined,
  });
};

export const useFetchInfiniteSelectableParentObjectives = (
  objectivePeriodId: number | undefined,
  userId: number | undefined,
  visibility?: ObjectiveVisibility,
  search?: SearchRequest,
) =>
  useInfiniteQuery(
    fetchInfiniteSelectableParentObjectivesQueryOptions(
      objectivePeriodId,
      userId,
      visibility,
      search,
    ),
  );
export const fetchObjectivePeriodMeSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...objectivePeriods.meSummary(id),
    queryFn: () =>
      api
        .get<ObjectiveTypesMeSummary>(
          `/performance/objective-periods/${id}/me/summary`,
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchObjectivePeriodMeSummary = (id?: number) =>
  useQuery(fetchObjectivePeriodMeSummaryQueryOptions(id));

export const fetchObjectiveSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...objectivePeriods.objectivesSummary(id),
    queryFn: () =>
      api
        .get<ObjectiveSummary | []>(
          `/performance/objective-periods/${id}/objectives/summary`,
          {
            params: {
              include,
            },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchObjectiveSummary = (id?: number) =>
  useQuery(fetchObjectiveSummaryQueryOptions(id));

export const fetchObjectivesStatusSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...objectivePeriods.statusSummary(id),
    queryFn: () =>
      api
        .get<ObjectivesStatusSummaryResponse>(
          `/performance/objective-periods/${id}/objectives/status/summary`,
          {
            params: {
              include,
            },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchObjectivesStatusSummary = (id?: number) =>
  useQuery(fetchObjectivesStatusSummaryQueryOptions(id));

export const fetchObjectivesUnitsSummaryQueryOptions = (id?: number) =>
  infiniteQueryOptions({
    ...objectivePeriods.unitsSummary(id),
    queryFn: ({ pageParam }) =>
      api
        .get<ObjectivesunitsSummaryResponse>(
          `/performance/objective-periods/${id}/objectives/units-summary`,
          {
            params: {
              include,
              page: pageParam,
            },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
    enabled: id !== undefined,
  });

export const useFetchObjectivesunitsSummary = (id?: number) =>
  useInfiniteQuery({ ...fetchObjectivesUnitsSummaryQueryOptions(id) });
